import { ErrorHandler, inject, Injectable }                                                   from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { firstValueFrom, from, Observable }                                                   from 'rxjs';

import { UserData }                            from '../../../api/user/user-data';
import { UserService }                         from '../../../api/user/user.service';
import { SessionService }                      from '../../../api/session/session.service';
import { InternalRoutes, InternalRoutesUtils } from '../../../shared/data/internal-routes';
import { StringUtils }                         from '../../../shared/utils/string.utils';
import { AuthenticationService }               from '../authentication.service';

@Injectable()
export class AuthGuard implements CanActivate, CanActivateChild {
  private authService = inject(AuthenticationService);
  private router = inject(Router);
  private sessionService = inject(SessionService);
  private userService = inject(UserService);
  private errorHandler = inject(ErrorHandler);

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return from(this.isUserAllowed(state));
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.canActivate(childRoute, state);
  }

  private async isUserAllowed(state: RouterStateSnapshot): Promise<boolean> {
    try {
      return await this.isUserAllowedToAccessUrl(state.url);
    } catch (e) {
      this.errorHandler.handleError(e);
    }
  }

  private async isUserAllowedToAccessUrl(url: string): Promise<boolean> {
    const sessionChangedData = await firstValueFrom(this.authService.isAuthenticated());
    if (!sessionChangedData) {
      // case when not authenticated
      if (url.startsWith(InternalRoutes.ENTER_VERIFICATION_CODE)) {
        return true;
      }
      await this.router.navigate(
        ['login'],
        !InternalRoutesUtils.isDefaultPageRoute(url) ? {queryParams: {returnUrl: url}} : {});
      this.authService.publishReloadLoginEvent();
      return false;
    }
    if (sessionChangedData?.sso) {
      if (InternalRoutesUtils.routeIsNotVisibleBySsoUsers(url)) {
        await this.router.navigate([InternalRoutes.ERROR]);
        return false;
      }
      return true;
    }
    const needsSetupChecks = !InternalRoutesUtils.routeIsNotOverridableByMfaSetup(url);
    const sessionPromise = AuthGuard.ignoreRejection(firstValueFrom(this.sessionService.loadSession()));
    const mfaOptionsPromise = needsSetupChecks
      ? AuthGuard.ignoreRejection(firstValueFrom(this.authService.getUserMfaOptions()))
      : null;
    const userSession = await sessionPromise;
    const remediation = await this.enforceUsernameRemediation(url, userSession?.duplicateUsername);
    if (remediation !== undefined) {
      return remediation;
    }
    if (!needsSetupChecks) {
      return true;
    }
    if (!userSession?.dcpMonitor && !(await this.showMfaSetup(mfaOptionsPromise))) {
      return false;
    }
    return this.showFirstAndLastNameSetup(url, firstValueFrom(this.userService.getUserData()));
  }
  private async enforceUsernameRemediation(url: string, duplicateUsername: boolean): Promise<boolean | undefined> {
    const isDuplicate = !!duplicateUsername;
    const onRemediation = url.startsWith(InternalRoutes.USERNAME_REMEDIATION);
    if (isDuplicate && !onRemediation) {
      await this.router.navigate([InternalRoutes.USERNAME_REMEDIATION]);
      return false;
    }
    // Non-duplicate user shouldn't sit on the remediation screen.
    if (!isDuplicate && onRemediation) {
      await this.router.navigate([InternalRoutes.ACCOUNTS]);
      return false;
    }
    if (isDuplicate && onRemediation) {
      return true;
    }
    return undefined;
  }
  private async showMfaSetup(mfaOptionsPromise: Promise<string[]>): Promise<boolean> {
    const mfaOptions = await mfaOptionsPromise;
    if (mfaOptions === undefined || mfaOptions.length === 0) {
      await this.router.navigate([InternalRoutes.SETUP_SECURITY_TOKEN]);
      return false;
    }

    return true;
  }
  private async showFirstAndLastNameSetup(url: string, userDataPromise: Promise<UserData>): Promise<boolean> {
    const areNamesSet = await this.areNamesSet(userDataPromise);
    if (url.startsWith(InternalRoutes.SETUP_NAMES)) {
      if (areNamesSet) {
        await this.router.navigate([InternalRoutes.ERROR]);
        return false;
      }
      return true;
    }
    if (!areNamesSet) {
      await this.router.navigate([InternalRoutes.SETUP_NAMES]);
      return false;
    }
    return true;
  }
  private async areNamesSet(userDataPromise: Promise<UserData>): Promise<boolean> {
    const userInfo: UserData = await userDataPromise;
    return !StringUtils.isNullOrEmpty(userInfo?.firstName) && !StringUtils.isNullOrEmpty(userInfo?.lastName);
  }
  private static ignoreRejection<T>(promise: Promise<T>): Promise<T> {
    promise.catch(() => undefined);
    return promise;
  }
}
